import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminApi } from '../../../services/api';

/**
 * User Preferences Redux Slice
 *
 * Manages user preferences for UI settings across the application.
 * Preferences are loaded once on login and kept in memory for fast access.
 * Updates are saved asynchronously to the backend.
 *
 * Preference Structure:
 * {
 *   vcx_events_filters: {
 *     default_status: 'active',
 *     default_outcome: ',new,in_progress',
 *     default_from_date: 'today',
 *     default_to_date: 'today',
 *     ...
 *   },
 *   ag_grid_layouts: {
 *     vcx_events_grid: {
 *       columnState: [...],
 *       filterModel: {...},
 *       ...
 *     },
 *     ...
 *   },
 *   ...
 * }
 */

// Async thunks
export const loadUserPreferences = createAsyncThunk(
  'preferences/load',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminApi.getUserPreferences();
      return response.data.preferences || {};
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to load user preferences' });
    }
  }
);

export const saveUserPreferences = createAsyncThunk(
  'preferences/save',
  async (userPreferences, { rejectWithValue }) => {
    try {
      const response = await adminApi.updateUserPreferences(userPreferences);
      return response.data.preferences || {};
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to save user preferences' });
    }
  }
);

const initialState = {
  data: {},
  loading: false,
  saving: false,
  loaded: false,
  error: null,
  lastSaved: null,
};

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    // Update preferences in memory (will be saved automatically via middleware or manual save)
    updateUserPreferences: (state, action) => {
      state.data = { ...state.data, ...action.payload };
    },
    // Update a specific preference key
    updateUserPreferenceKey: (state, action) => {
      const { key, value, merge = false } = action.payload;

      // If merge flag is true, do deep merge, otherwise replace
      if (merge && typeof value === 'object' && value !== null && !Array.isArray(value)) {
        state.data[key] = {
          ...(state.data[key] || {}),
          ...value
        };
      } else {
        // Direct replacement (used for deletions and updates)
        state.data[key] = value;
      }
    },
    // Clear all preferences
    clearUserPreferences: (state) => {
      state.data = {};
      state.loaded = false;
      state.error = null;
    },
    // Mark preferences as dirty (need saving)
    markDirty: (state) => {
      state.needsSave = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load preferences
      .addCase(loadUserPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUserPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.data = action.payload;
      })
      .addCase(loadUserPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to load user preferences';
      })
      // Save preferences
      .addCase(saveUserPreferences.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUserPreferences.fulfilled, (state) => {
        state.saving = false;
        // Don't overwrite state.data - the optimistic update already applied the correct changes
        // This prevents the grid from reloading/reverting when the API responds
        // Redux Persist will save the optimistic update to localStorage for page refresh
        state.lastSaved = new Date().toISOString();
        state.needsSave = false;
      })
      .addCase(saveUserPreferences.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload?.message || 'Failed to save user preferences';
      });
  },
});

export const {
  updateUserPreferences,
  updateUserPreferenceKey,
  clearUserPreferences,
  markDirty,
} = userPreferencesSlice.actions;

// Selectors
export const selectUserPreferences = (state) => state.userPreferences.data;
export const selectUserPreferencesLoading = (state) => state.userPreferences.loading;
export const selectUserPreferencesSaving = (state) => state.userPreferences.saving;
export const selectUserPreferencesLoaded = (state) => state.userPreferences.loaded;
export const selectUserPreferencesError = (state) => state.userPreferences.error;
export const selectUserPreferenceKey = (key) => (state) => state.userPreferences.data[key];

// Stable empty object reference to prevent re-renders
const EMPTY_FILTERS = {};

// Utility selectors for common preferences
export const selectVCXEventsFilters = (state) => state.userPreferences.data.vcx_events_filters || EMPTY_FILTERS;
export const selectAgGridLayout = (gridName) => (state) =>
  state.userPreferences.data.ag_grid_layouts?.[gridName] || null;

export default userPreferencesSlice.reducer;
